/**
 * EditTemplateDialog Component (Story 5.14, Task 6)
 *
 * Modal dialog for editing custom template metadata (name, description, category).
 * Pre-fills from existing template data.
 *
 * Only shown for custom templates (isSystemTemplate === 0).
 */

import { useState, useEffect, useRef } from "react";
import { PencilSimple } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@provenleap/ui";
import { useTemplateMutations } from "../../hooks/useTemplateMutations";

const CATEGORY_OPTIONS = [
  { value: "analytics", label: "Analytics" },
  { value: "seo", label: "SEO" },
  { value: "advertising", label: "Advertising" },
  { value: "social", label: "Social" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "multi-channel", label: "Multi-channel" },
];

interface EditTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  initialName: string;
  initialDescription: string | null;
  initialCategory: string | null;
  onSuccess?: () => void;
}

export function EditTemplateDialog({
  open,
  onOpenChange,
  templateId,
  initialName,
  initialDescription,
  initialCategory,
  onSuccess,
}: EditTemplateDialogProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription ?? "");
  const [category, setCategory] = useState(initialCategory ?? "");
  const [nameError, setNameError] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);

  const { updateTemplate } = useTemplateMutations();

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setDescription(initialDescription ?? "");
    setCategory(initialCategory ?? "");
    setNameError("");
    const timer = setTimeout(() => nameInputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [open, initialName, initialDescription, initialCategory]);

  const validate = (): boolean => {
    if (!name.trim()) {
      setNameError("Name is required");
      return false;
    }
    if (name.length > 200) {
      setNameError("Name must be 200 characters or fewer");
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (updateTemplate.isPending) return;

    updateTemplate.mutate(
      {
        id: templateId,
        name: name.trim(),
        description: description.trim() || null,
        category: category || null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilSimple size={20} />
            Edit Template
          </DialogTitle>
          <DialogDescription>
            Update the template name, description, or category.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Template Name */}
          <div>
            <label
              htmlFor="edit-template-name"
              className="block text-sm font-medium mb-1.5"
            >
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={nameInputRef}
              id="edit-template-name"
              data-testid="edit-template-name-input"
              type="text"
              maxLength={200}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError("");
              }}
              onKeyDown={handleKeyDown}
              className={`w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors
                focus:ring-2 focus:ring-primary/20 focus:border-primary
                ${nameError ? "border-red-500" : "border-input"}`}
              disabled={updateTemplate.isPending}
            />
            {nameError && (
              <p className="text-xs text-red-500 mt-1">{nameError}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="edit-template-description"
              className="block text-sm font-medium mb-1.5"
            >
              Description
            </label>
            <textarea
              id="edit-template-description"
              data-testid="edit-template-description-input"
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none transition-colors
                focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              disabled={updateTemplate.isPending}
            />
          </div>

          {/* Category */}
          <div>
            <label
              htmlFor="edit-template-category"
              className="block text-sm font-medium mb-1.5"
            >
              Category
            </label>
            <select
              id="edit-template-category"
              data-testid="edit-template-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none transition-colors
                focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background"
              disabled={updateTemplate.isPending}
            >
              <option value="">None</option>
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Error */}
          {updateTemplate.error && (
            <p className="text-sm text-red-500 bg-red-50 rounded-md px-3 py-2">
              {updateTemplate.error.message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateTemplate.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleSave}
            disabled={updateTemplate.isPending || !name.trim()}
            data-testid="edit-template-confirm"
          >
            {updateTemplate.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
